import GeneratedAvatar from "@/components/users/GeneratedAvatar";

/** Default GeneratedAvatar size, kept in sync with GeneratedAvatar's own default. */
const DEFAULT_SIZE = 28;

interface UserAvatarProps {
  /** Clerk user.imageUrl — always non-empty; ignored unless hasImage is true. */
  imageUrl?: string;
  /** Clerk user.hasImage — true only when the user actually uploaded a photo. */
  hasImage?: boolean;
  /**
   * The caller's NUMERIC app user id (`usePermissions().userId`) — the same seed every other
   * surface uses (UsersBlotter, SwitchUserModal), so one person gets one avatar everywhere.
   * Never Clerk's string id: it hashes to a different config than the users table does.
   * Null/undefined while permissions are in flight.
   */
  seed: number | null | undefined;
  /** Rendered size in px. The ONLY source of geometry — see `className`. */
  size?: number;
  alt?: string;
  /**
   * Decoration only (border/ring/shadow). Do NOT pass width/height utilities: `size` owns
   * geometry via an inline style on the same element, which would silently win over them.
   */
  className?: string;
}

/**
 * UserAvatar — the signed-in user's avatar. Renders the user's own uploaded photo when
 * Clerk reports one (hasImage), otherwise falls back to the GFG geometric GeneratedAvatar
 * instead of Clerk's purple-gradient initials image. The fallback is picked deterministically
 * from `seed` so the same user gets the same avatar everywhere.
 *
 * `seed` arrives from `usePermissions()` and is null/undefined until that query resolves.
 * Rendering a GeneratedAvatar before then would show a real (wrong) person's avatar for an
 * instant, so while the seed is unknown (and there's no photo to show instead) this renders a
 * neutral, identity-less placeholder in the same box instead of guessing a seed.
 *
 * All three branches share ONE wrapper, so a photo user, a generated-avatar user, and the
 * loading placeholder all render as the same size, the same clipped circle, and carry the
 * consumer's ring identically.
 */
export default function UserAvatar({
  imageUrl,
  hasImage,
  seed,
  size = DEFAULT_SIZE,
  alt,
  className,
}: UserAvatarProps) {
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: "9999px",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {hasImage && imageUrl ? (
        <img
          src={imageUrl}
          alt={alt ?? "User avatar"}
          // Fills the wrapper rather than sizing itself, so the wrapper stays the one place
          // geometry is decided.
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : seed !== null && seed !== undefined ? (
        <GeneratedAvatar id={seed} size={size} alt={alt} />
      ) : (
        <span
          aria-hidden="true"
          className="bg-smoke"
          style={{ width: "100%", height: "100%", display: "block" }}
        />
      )}
    </span>
  );
}
